// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBook,
    faUpload,
    faRightFromBracket,
    faUser,
    faRobot,
    faWandMagicSparkles,
    faBars,
    faXmark,
    faCalendar,
    faHistory,
    faCog,
    faUsers,
    faHome,
    faPlug,
    faQuestionCircle,
    faChartLine,
    faGlobe,
    faCreditCard,
    faBolt,
    faDollarSign,
    faCode,
    faEye,
    faLifeRing,
    faFlask,
    faBrain,
    faGraduationCap
} from '@fortawesome/free-solid-svg-icons';
import ImpersonationBanner from '../components/ImpersonationBanner';
import UsageIndicator from '../components/UsageIndicator';
import { getClientProfile, getCitationSettings, logoutUser } from '../services/api';

interface LayoutProps {
    onShowChat: () => void;
    clientId: string | null;
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ onShowChat, clientId, children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [citationsEnabled, setCitationsEnabled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    
    // Keep profile state for the header icon
    const [profile, setProfile] = useState(null);
    const userRole = localStorage.getItem('user_role');

    // Fetch profile data
    const fetchProfile = async () => {
        try {
            const clientId = localStorage.getItem('client_id');
            if (clientId) {
                const data = await getClientProfile(clientId);
                setProfile(data);
            }
        } catch (error) {
            console.error('Failed to load profile:', error);
        }
    };

    // Check citation status
    const checkCitationStatus = async () => {
        try {
            const clientId = localStorage.getItem('client_id');
            if (clientId) {
                console.log('[CITATION-DEBUG] Checking citation status for client:', clientId);
                const data = await getCitationSettings(clientId);
                console.log('[CITATION-DEBUG] Citation status response:', data);
                
                // Force citationsEnabled to true if the API returns success and citations_enabled is true
                if (data.success && data.citations_enabled === true) {
                    console.log('[CITATION-DEBUG] Setting citations enabled to TRUE');
                    setCitationsEnabled(true);
                    // Also store in localStorage
                    localStorage.setItem('citations_enabled', 'true');
                } else {
                    console.log('[CITATION-DEBUG] Setting citations enabled to FALSE');
                    setCitationsEnabled(false);
                    localStorage.setItem('citations_enabled', 'false');
                }
            }
        } catch (error) {
            console.error('[CITATION-DEBUG] Error checking citation status:', error);
        }
    };

    // Initialize citations status from localStorage if possible
    useEffect(() => {
        const storedCitationsEnabled = localStorage.getItem('citations_enabled');
        if (storedCitationsEnabled === 'true') {
            console.log('[CITATION-DEBUG] Initialize citations enabled to TRUE from localStorage');
            setCitationsEnabled(true);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
        checkCitationStatus();

        // Add event listener for citation settings changes
        const handleCitationSettingsChange = () => {
            console.log('[DEBUG] Citation settings changed event received');
            checkCitationStatus();
        };

        // Add event listener for profile refresh (e.g., after client creation/crawl completion)
        const handleProfileRefresh = () => {
            console.log('[DEBUG] Profile refresh event received');
            fetchProfile();
        };

        window.addEventListener('citation-settings-changed', handleCitationSettingsChange);
        window.addEventListener('profile-data-changed', handleProfileRefresh);

        return () => {
            window.removeEventListener('citation-settings-changed', handleCitationSettingsChange);
            window.removeEventListener('profile-data-changed', handleProfileRefresh);
        };
    }, []);

    // Refresh profile when route changes (ensures fresh data after navigation)
    useEffect(() => {
        fetchProfile();
    }, [location.pathname]);


    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            // Auto-close sidebar on mobile
            if (mobile) setIsSidebarOpen(false);
            else setIsSidebarOpen(true);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Hide sidebar on route change for mobile
    useEffect(() => {
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    }, [location.pathname, isMobile]);

    const isActive = (path: string) => location.pathname === path;

    const MenuItem = ({ to, icon, text, disabled = false }) => {
        if (disabled) {
            return (
                <div
                    className="flex items-center px-6 py-3 my-1 rounded-lg transition-colors cursor-not-allowed"
                    title="Client ID is required"
                    style={{ color: 'rgb(243, 244, 246)' }}
                >
                    <FontAwesomeIcon icon={icon} className="w-5 h-5" style={{ color: 'rgb(243, 244, 246)' }} />
                    <span className="ml-3 font-medium">{text}</span>
                </div>
            );
        }
        
        return (
            <Link
                to={to}
                className={`flex items-center px-6 py-3 my-1 rounded-lg transition-colors group ${
                    isActive(to) 
                        ? 'bg-white shadow-sm' 
                        : 'hover:bg-white hover:shadow-sm'
                }`}
                style={isActive(to) ? { color: 'black' } : { color: 'rgb(243, 244, 246)' }}
                onMouseEnter={(e) => {
                    if (!isActive(to)) {
                        const icon = e.currentTarget.querySelector('.fa-icon');
                        const text = e.currentTarget.querySelector('.menu-text');
                        if (icon) icon.style.color = 'rgb(234, 179, 8)';
                        if (text) text.style.color = 'black';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isActive(to)) {
                        const icon = e.currentTarget.querySelector('.fa-icon');
                        const text = e.currentTarget.querySelector('.menu-text');
                        if (icon) icon.style.color = 'rgb(243, 244, 246)';
                        if (text) text.style.color = 'rgb(243, 244, 246)';
                    }
                }}
            >
                <FontAwesomeIcon 
                    icon={icon} 
                    className="w-5 h-5 transition-colors fa-icon"
                    style={isActive(to) ? { color: 'rgb(234, 179, 8)' } : { color: 'rgb(243, 244, 246)' }}
                />
                <span 
                    className="ml-3 font-medium transition-colors menu-text"
                    style={isActive(to) ? { color: 'black' } : { color: 'rgb(243, 244, 246)' }}
                >{text}</span>
            </Link>
        );
    };

    const getNavigationItems = () => {
        // Super admin navigation items
        if (userRole === 'super') {
            return [
                { path: '/consultant/dashboard', icon: faHome, label: 'Dashboard' },
                { path: '/consultant/client-form', icon: faUsers, label: 'Add Client' },
                { path: '/consultant/support-flows', icon: faLifeRing, label: 'Support Assistant' },
                { path: '/consultant/seo-center', icon: faChartLine, label: 'SEO Dashboard' },
                { path: '/consultant/plan-management', icon: faCreditCard, label: 'Plans & Payments' },
                { path: '/consultant/pipeline', icon: faCog, label: 'Pipeline' },
                { path: '/consultant/role-management', icon: faCog, label: 'Roles' },
                { path: '/consultant/consultants', icon: faUsers, label: 'Consultants' },
                // { path: '/consultant/agents', icon: faRobot, label: 'AI Agents' },
            ];
        }
        
        // Consultant navigation items
        if (userRole === 'consultant') {
            return [
                { path: '/consultant/dashboard', icon: faHome, label: 'Dashboard' },
                { path: '/consultant/client-form', icon: faUsers, label: 'Add Client' },
                { path: '/consultant/support-flows', icon: faLifeRing, label: 'Support Assistant' },
                { path: '/consultant/seo-center', icon: faGlobe, label: 'SEO Center' },
                // { path: '/consultant/agents', icon: faRobot, label: 'AI Agents' },
            ];
        }

        // For client and client_admin roles
        const isImpersonating = !!localStorage.getItem('impersonation_token');
        
        // All items available to clients (except theme editor)
        const clientItems = [
            { path: '/dashboard', icon: faHome, label: 'Dashboard' },
            { path: '/knowledge-base', icon: faBook, label: 'Knowledge Base' },
            { path: '/chatbot-training', icon: faBrain, label: 'Chatbot Training' },
            { path: '/re-upload', icon: faUpload, label: 'Data Import' },
            { path: '/integrations', icon: faPlug, label: 'Integrations' },
            { path: '/calendar-settings', icon: faCalendar, label: 'Calendar' },
            { path: '/settings', icon: faCog, label: 'Settings' },
            // { path: '/profile', icon: faUser, label: 'Profile' },
        ];
        
        // Billing always visible for clients (including when super user is impersonating)
        const customerOnlyItems = [
            { path: '/client/billing', icon: faCreditCard, label: 'Billing' },
        ];
        
        // Theme editor only visible during impersonation
        const impersonationOnlyItems = [
            { path: '/theme-editor', icon: faWandMagicSparkles, label: 'Theme Editor' },
        ];
        
        return [
            ...clientItems,
            ...customerOnlyItems,
            ...(isImpersonating ? impersonationOnlyItems : []),
        ];
    };

    const handleLogout = () => {
        console.log('Logging out');
        logoutUser('/login');
    };

    return (
        <div className="min-h-screen" style={{ backgroundImage: "url('/background.jpg')", backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }}>
            {isMobile && (
                <button
                    className="fixed top-4 left-4 z-[95] p-2 rounded-lg bg-blue-600 text-white"
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                >
                    <FontAwesomeIcon icon={isSidebarOpen ? faXmark : faBars} />
                </button>
            )}

            {/* Sidebar Overlay for Mobile */}
            {isMobile && isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <div className={`
                fixed top-0 left-0 h-full text-white transition-transform duration-300 ease-in-out z-[100]
                w-[250px] bg-[#065BDF] flex flex-col rounded-tr-[50px] rounded-br-[50px]
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                ${isMobile ? 'shadow-lg' : ''}
            `}>
                {/* Existing sidebar content */}
                <div className="h-full flex flex-col p-4">
                    {/* Logo - centered without icon space offset */}
                    <div className="flex items-center justify-center mb-6">
                        <a
                            href="https://www.brndsh.io/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:opacity-80 transition-opacity"
                        >
                            <img
                                src="https://cdn.prod.website-files.com/6703f95fee8846f901ef895c/672327f7cee1ee616023f024_Vectors-Wrapper.svg"
                                loading="lazy"
                                width="120"
                                height="auto"
                                alt="BRNDSH"
                                className="logo"
                            />
                        </a>
                    </div>

                    {/* Usage Indicator - Only for client users */}
                    {(userRole === 'client' || userRole === 'client_admin') && (
                        <div className="mb-4">
                            <UsageIndicator />
                        </div>
                    )}

                    {/* Navigation Items - with scrollbar */}
                    <nav className="flex-1 overflow-y-auto overflow-x-hidden sidebar-with-scroll pr-2">
                        {getNavigationItems().map((item) => (
                            <MenuItem
                                key={item.path}
                                to={item.path}
                                icon={item.icon}
                                text={item.label}
                                disabled={item.disabled}
                            />
                        ))}
                    </nav>

                    {/* Logout Button */}
                    <div className="p-4 mt-auto">
                        <button 
                            onClick={handleLogout}
                            className="flex items-center px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg w-full"
                        >
                            <FontAwesomeIcon 
                                icon={faRightFromBracket} 
                                className="w-6 h-6 mr-3 text-yellow-400"
                            />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Impersonation Banner - Full width behind sidebar */}
            {localStorage.getItem('impersonation_token') && (
                <div className="fixed top-0 left-0 w-full z-[90]">
                    <ImpersonationBanner />
                </div>
            )}

            {/* Main Content */}
            <div className={`
                transition-all duration-300 ease-in-out
                ${isSidebarOpen ? 'md:ml-[250px]' : 'ml-0'}
                ${localStorage.getItem('impersonation_token') ? 'pt-12' : ''}
                lg:pb-16
            `}>
                {/* Top Navigation */}
                <div className="h-16 flex items-center justify-between px-4 md:px-8">
                    {(userRole === 'client' || userRole === 'client_admin' || localStorage.getItem('impersonation_token')) && (
                        <>
                            {/* Left side - AI Assistant button */}
                            <div className="flex items-center">
                                <button
                                    onClick={onShowChat}
                                    className="ml-12 md:ml-0 mr-4 px-4 py-2 hover:bg-blue-50 rounded-xl flex items-center text-[#065BDF] transition-all duration-200 border border-blue-100 hover:border-blue-200 shadow-sm hover:shadow-md"
                                >
                                    <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
                                        <FontAwesomeIcon icon={faRobot} className="text-white text-sm" />
                                    </div>
                                    <div className="text-left">
                                        <div className="font-semibold text-sm">AI Assistant</div>
                                        <div className="text-xs text-blue-500">Ask me anything</div>
                                    </div>
                                </button>
                            </div>

                            {/* Right side - Preview and Profile buttons */}
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => {
                                        const website = profile?.website_url || 'https://example.com';
                                        const url = `/preview/${clientId}?url=${encodeURIComponent(website)}`;
                                        window.open(url, '_blank');
                                    }}
                                    className="px-4 py-2 hover:bg-green-50 rounded-xl flex items-center text-green-600 transition-all duration-200 border border-green-100 hover:border-green-200 shadow-sm hover:shadow-md"
                                    title="Preview your website with widget"
                                >
                                    <FontAwesomeIcon icon={faEye} className="mr-2 text-sm" />
                                    <span className="font-semibold text-sm">Preview</span>
                                </button>

                                <button
                                    onClick={() => navigate(userRole === 'client' || userRole === 'client_admin'
                                        ? '/profile'
                                        : '/consultant/profile')}
                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                                >
                                    {profile?.profile_image ? (
                                        <img
                                            src={profile.profile_image.startsWith('http')
                                                ? profile.profile_image
                                                : `${process.env.REACT_APP_API_URL}/${profile.profile_image}`}
                                            alt="Profile"
                                            className="w-full h-full rounded-full object-cover"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <FontAwesomeIcon icon={faUser} className="text-gray-600" />
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                    {(userRole === 'consultant' || userRole === 'super') && (
                        <div className="ml-auto flex items-center space-x-3" />
                    )}
                </div>

                {/* Page Content - Using Outlet */}
                <div className="p-4 md:p-8">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Layout; 
